import moment from 'moment';
import {
  ILoginActions,
  IPouchActions,
  IVenueActions,
  PouchDocType,
  VenueActionNames,
  BlockoutRepeatTypes,
  BlockoutType,
  IBlockout,
} from 'shared-types/index';
import DateUtilsService from 'external-services/date-utils-service';
import { LocaleService } from 'internal-services/locale/locale.service';
import AdminService from 'external-services/diary-admin-service/admin.service';
import PouchLoadService from 'external-services/pouch-load-service';
import GridSyncService from 'app/services/gridSync/gridSync.service';
import { IHidingServicesDto } from 'external-services/blockouts-service/types';
import { appURLS } from 'appRoot/app.constants';
import { AxiosInstance } from 'axios';
import NetworkRequestsService from 'external-services/network-requests-service';
import { DAY_END } from 'external-services/date-utils-service/types';

const NS = 'BlockOutsService'; // jshint ignore:line
const api: AxiosInstance = NetworkRequestsService.getGlobalAPI();

export interface IBlockoutDocData {
  offset: number;
  total_rows: number;
  rows: {
    doc: IBlockout;
    id: string;
    key: string;
    value: { rev: string };
  }[];
}
class BlockOutsService {
  private blockOutsCache: IBlockoutDocData;

  private $filter: any;
  private getDBOnlineReducedAsFirstOption: () => PouchDB.Database;
  private trackEvent?: (eventName: any, values?: any) => void;
  private trackError?: (error: any) => void;

  private PouchActions: IPouchActions;
  private LoginActions: ILoginActions;
  private VenueActions: IVenueActions;

  private getTimezoneId: () => string;
  private getVenueId: () => number;
  private getAccountId: () => string;

  public setup(
    PouchActions: IPouchActions,
    LoginActions: ILoginActions,
    VenueActions: IVenueActions,
    $filter: any,
    getDBOnlineReducedAsFirstOption: () => PouchDB.Database,
    trackEvent?: (eventName: any, values?: any) => void,
    trackError?: (error: Error) => void
  ): void {
    this.PouchActions = PouchActions;
    this.LoginActions = LoginActions;
    this.VenueActions = VenueActions;
    this.$filter = $filter;
    this.getDBOnlineReducedAsFirstOption = getDBOnlineReducedAsFirstOption;
    this.trackEvent = trackEvent;
    this.trackError = trackError;
  }

  public init(getTimezoneId: () => string, getVenueId: () => number, getAccountId: () => string): void {
    this.getTimezoneId = getTimezoneId;
    this.getVenueId = getVenueId;
    this.getAccountId = getAccountId;
    this.PouchActions.handlePouchSyncedDoctype(PouchDocType.blockout, () => this.clearCache());
    this.LoginActions.handleLogout(() => this.clearCache());
    this.VenueActions.handleVenueAction(VenueActionNames.VENUE_CHANGE, () => this.clearCache());
  }

  public clearCache(): void {
    console.log('[BLOCKOUT UPDATE] - Cleaning blockouts cache');
    this.blockOutsCache = null;
  }

  public getById(id: string): Promise<IBlockout> {
    return this.getDBOnlineReducedAsFirstOption()
      .get(id)
      .then((blockout) => {
        return this.mapModelToView(blockout as IBlockout);
      });
  }
  public async saveHiddenService(hidingServicesDto: IHidingServicesDto) {
    try {
      const res = await api.post(appURLS.API + `/accounts/${this.getAccountId()}/venues/${this.getVenueId()}/hidden-service/create`, hidingServicesDto)
      return res.data;
    } catch (error) {
      console.error('saveHiddenService error==>', error);
      throw error;
    }
  }
  public async deleteHiddenService(hidingServicesDto: IHidingServicesDto) {
    try {
      const res = await api.post(appURLS.API + `/accounts/${this.getAccountId()}/venues/${this.getVenueId()}/hidden-service/delete`, hidingServicesDto)
      return res.data;
    } catch (error) {
      console.error('deleteHiddenService error==>', error);
      throw error;
    }
  }
  public async getHiddenServiceByDate(date: string): Promise<IHidingServicesDto> {
    try {
      const res = await api.post(appURLS.API + `/accounts/${this.getAccountId()}/venues/${this.getVenueId()}/hidden-service/query`, { date })
      return res.data;
    } catch (error) {
      console.error('getHiddenServiceByDate error==>', error);
      throw error;
    }
  }


  public getAll(): Promise<IBlockout[]> {
    return new Promise((resolve) => {
      if (this.blockOutsCache) {
        resolve(this.updateAndLoadFromCache());
      } else {
        Promise.resolve(
          this.getDBOnlineReducedAsFirstOption()
            .allDocs({
              include_docs: true,
              attachments: false,
              startkey: 'blockout_',
              endkey: 'blockout_\uffff',
            })
            .then((blockoutsData) => {
              console.log('[BLOCKOUT UPDATE] - Refreshing blockouts list');
              resolve(this.updateAndLoadFromCache(blockoutsData as IBlockoutDocData));
            })
        );
      }
    });
  }

  public save(blockout: IBlockout): Promise<PouchDB.Core.Response> {
    blockout = this.mapViewToModel(blockout);

    // CLI-165: a blockout with no _id yet is a create, otherwise an edit.
    const isNewBlockout = !blockout._id;

    if (!blockout._id) {
      // is a new block out add an ID
      blockout._id = PouchLoadService.getNewId(['blockout']);
      blockout.createdDate = new Date().toISOString();
      this.trackEvent('New Blockout', {
        id: blockout._id,
      });
    } else {
      blockout.updatedDate = new Date().toISOString();
      this.trackEvent('Edit Blockout', {
        id: blockout._id,
        rev: blockout._rev,
      });
    }

    if (blockout.repeatType === BlockoutRepeatTypes.Close) {
      const start = DateUtilsService.dateTimeStrToDate(blockout.startDate as string);

      //If Start time is entered by the user, then set that value to startDate
      this.setDate(start, blockout.startTime?.toString());

      const end = DateUtilsService.dateTimeStrToDate(blockout.endDate as string);

      const dateIsDefault = this.setDate(end, blockout.endTime?.toString());

      if (dateIsDefault) {
        end.setDate(end.getDate() + 1);
      }

      blockout.startDate = DateUtilsService.dateToDateTimeStr(start);
      blockout.startDateTime = DateUtilsService.dateToDateTimeStr(start) as unknown as Date;

      blockout.endDateTime = DateUtilsService.dateToDateTimeStr(end) as unknown as Date;
      blockout.endDate = DateUtilsService.dateToDateTimeStr(end);
    }

    this.clearCache();
    return Promise.resolve(this.getDBOnlineReducedAsFirstOption().put(blockout)).then(
      (response) => {
        // CLI-165: notify the Grid Sync API of the change
        const entityId = (response && response.id) || blockout._id;
        if (isNewBlockout) {
          GridSyncService.blockOutCreated(entityId);
        } else {
          GridSyncService.blockOutEdited(entityId);
        }
        return response;
      }
    );
  }

  private setDate(dateToModify: Date, hoursData: string): boolean {
    if (hoursData) {
      const startDate = hoursData.split(':');
      dateToModify.setHours(Number(startDate[0]));
      dateToModify.setMinutes(Number(startDate[1]));

      //False to signify user had selected a time value for the close date.
      return false;
    } else {
      //I think we are setting to 4AM as the start time as thats the service start time for the day
      dateToModify.setHours(DAY_END);
      dateToModify.setMinutes(0);
    }

    return true;
  }

  public remove(blockout: IBlockout): Promise<PouchDB.Core.Response> {
    this.trackEvent('Delete Blockout', {
      id: blockout._id,
      rev: blockout._rev,
    });

    this.clearCache();
    const deletedBlockoutId = blockout._id;
    return Promise.resolve(
      this.getDBOnlineReducedAsFirstOption().remove(blockout as PouchDB.Core.RemoveDocument)
    ).then((response) => {
      // CLI-165: notify the Grid Sync API of the deletion
      GridSyncService.blockOutDeleted(deletedBlockoutId);
      return response;
    });
  }

  public updateAndLoadFromCache(optionalBlockoutData: IBlockoutDocData = null): IBlockout[] {
    if (optionalBlockoutData) {
      this.blockOutsCache = JSON.parse(JSON.stringify(optionalBlockoutData));
    }
    const blockoutsData: IBlockoutDocData = JSON.parse(JSON.stringify(this.blockOutsCache));
    return blockoutsData.rows.map((item: { doc: IBlockout }) => {
      return this.mapModelToView(item.doc);
    });
  }

  public getErrors(blockout: IBlockout): string[] {
    const e = [];
    if (!blockout.createdDate) {
      e.push('!createdDate');
    }
    if (blockout.endDateTime && !blockout.startDateTime) {
      e.push('endDateTime && !startDateTime');
    }
    if (blockout.startTime && !blockout.startDate) {
      e.push('startTime && !startDate');
    }
    if (blockout.endTime && !blockout.startDate) {
      e.push('endTime && !startDate');
    }

    return e;
  }

  public cleanBlockout(blockout: IBlockout): IBlockout {
    // Basic rules
    blockout.type = BlockoutType.blockout;
    blockout.repeatType = blockout.repeatType || BlockoutRepeatTypes.NoRepeat;
    blockout.weeklyDay = blockout.weeklyDay || {};
    blockout.excludedDates = blockout.excludedDates || [];
    blockout.blockedSections = blockout.blockedSections || [];
    blockout.blockedTables = blockout.blockedTables || [];

    // Type based rules
    if (
      blockout.repeatType !== BlockoutRepeatTypes.NoRepeat &&
      blockout.repeatType !== BlockoutRepeatTypes.Close
    ) {
      delete blockout.startDateTime;
      delete blockout.endDateTime;
    }
    if (blockout.repeatType === BlockoutRepeatTypes.NoRepeat) {
      delete blockout.startDate;
      delete blockout.endDate;
      blockout.excludedDates = [];
    }

    if (blockout.repeatType === BlockoutRepeatTypes.Close) {
      blockout.excludedDates = [];
      blockout.blockedSections = [];
      blockout.blockedTables = [];
    }
    if (blockout.repeatType !== BlockoutRepeatTypes.Close) {
      delete blockout.widgetMessage;
    }
    if (blockout.repeatType !== BlockoutRepeatTypes.Daily) {
      delete blockout.dailyRepeat;
    }
    if (blockout.repeatType !== BlockoutRepeatTypes.Weekly) {
      blockout.weeklyDay = {};
    }
    if (blockout.repeatType !== BlockoutRepeatTypes.Monthly) {
      delete blockout.monthlyType;
      delete blockout.monthlyOccurrence;
      delete blockout.monthlyDay;
      delete blockout.monthlyDate;
    }
    if (blockout.repeatType !== BlockoutRepeatTypes.Yearly) {
      delete blockout.yearlyMonth;
      delete blockout.yearlyDay;
    }

    return blockout;
  }

  public mapModelToView(blockout: IBlockout): IBlockout {
    blockout.startDateTimeStr = blockout.startDateTime?.toString();
    blockout.endDateTimeStr = blockout.endDateTime?.toString();
    // Report errors on model loaded from couch
    if (this.getErrors(blockout).length) {
      console.error('Invalid blockout', blockout, this.getErrors(blockout));
      this.trackError(new Error('Invalid blockout: ' + blockout._id));
    }

    blockout = this.cleanBlockout(blockout);

    try {
      if (blockout.startDateTimeStr) {
        blockout.startDateTime = DateUtilsService.dateTimeStrToDate(blockout.startDateTimeStr);
      }
      if (blockout.endDateTimeStr) {
        blockout.endDateTime = DateUtilsService.dateTimeStrToDate(blockout.endDateTimeStr);
      }
      if (
        blockout.endDateTime &&
        blockout.endDateTime.getTime() < blockout.startDateTime.getTime()
      ) {
        blockout.endDateTime.setDate(blockout.endDateTime.getDate() + 1);
      }
      if (blockout.startDate) {
        blockout.startDate = DateUtilsService.dateTimeStrToDate(blockout.startDate as string);
      }
      if (blockout.endDate) {
        blockout.endDate = DateUtilsService.dateTimeStrToDate(blockout.endDate as string);
      }
      if (blockout.startTime) {
        blockout.startTime = DateUtilsService.timeToDate(
          blockout.startTime,
          blockout.startDate as Date
        );
      }
      if (blockout.endTime) {
        blockout.endTime = DateUtilsService.timeToDate(
          blockout.endTime,
          blockout.startDate as Date
        );
      }
      if (blockout.excludedDates) {
        blockout.excludedDates = blockout.excludedDates.map((excludedDate: any) => {
          return DateUtilsService.dateTimeStrToDate(excludedDate);
        });
      } else {
        blockout.excludedDates = [];
      }
    } catch (e) {
      console.error('Format blockout failed', e);
      this.trackError(e);
    }

    return blockout;
  }

  public mapViewToModel(blockout: IBlockout): IBlockout {
    blockout = this.cleanBlockout(blockout);

    const timezoneId = this.getTimezoneId();

    // convert datetime to strings
    if (blockout.startDateTime) {
      blockout.startDateTime = DateUtilsService.dateToDateTimeStr(
        blockout.startDateTime
      ) as unknown as Date;
    }
    if (blockout.endDateTime) {
      blockout.endDateTime = DateUtilsService.dateToDateTimeStr(
        blockout.endDateTime
      ) as unknown as Date;
    }
    if (blockout.startTime) {
      blockout.startTime = DateUtilsService.dateToTimeStr(blockout.startTime as Date, 'HH:mm');
    }
    if (blockout.endTime) {
      blockout.endTime = DateUtilsService.dateToTimeStr(blockout.endTime as Date, 'HH:mm');
    }

    if (blockout.startDate) {
      blockout.startDate = DateUtilsService.getClearDate(blockout.startDate as Date, timezoneId);
      blockout.startDate = DateUtilsService.dateToDateTimeStr(blockout.startDate as Date);
    }

    if (blockout.endDate) {
      blockout.endDate = DateUtilsService.getClearDate(blockout.endDate as Date, timezoneId);
      blockout.endDate = DateUtilsService.dateToDateTimeStr(blockout.endDate as Date);
    } else {
      delete blockout.endDate;
    }
    if (blockout.excludedDates) {
      blockout.excludedDates = blockout.excludedDates.map((excludedDate: string | Date) => {
        return DateUtilsService.dateToDateTimeStr(excludedDate as Date);
      });
    }

    return blockout;
  }

  public getRepeatStr(blockout: IBlockout): string {
    const dateStrParts = [];
    const locale = LocaleService.getLocale();

    const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: 'long' });
    const monthFmt = new Intl.DateTimeFormat(locale, { month: 'long' });

    if (blockout.repeatType === BlockoutRepeatTypes.Daily) {
      dateStrParts.push(this.$filter('capitalizeFilter')(blockout.dailyRepeat));
    } else if (blockout.repeatType === BlockoutRepeatTypes.Weekly) {
      const days = [];
      for (const dayNum in blockout.weeklyDay) {
        const dayAllowed = blockout.weeklyDay[dayNum];
        if (dayAllowed) {
          // moment().day(n) navigates to the correct weekday in the current week
          days.push(weekdayFmt.format(moment().day(Number(dayNum)).toDate()) + 's');
        }
      }
      dateStrParts.push(days.join(', '));
    } else if (blockout.repeatType === BlockoutRepeatTypes.Monthly) {
      if (blockout.monthlyType === 'day') {
        dateStrParts.push(this.$filter('numberSuffixFilter')(blockout.monthlyOccurrence));
        dateStrParts.push(weekdayFmt.format(moment().day(Number(blockout.monthlyDay)).toDate()));
        dateStrParts.push('of the month,');
      } else {
        dateStrParts.push(this.$filter('numberSuffixFilter')(blockout.monthlyDate));
        dateStrParts.push('of the month,');
      }
    } else if (blockout.repeatType === BlockoutRepeatTypes.Yearly) {
      // moment().month(n) navigates to the correct month in the current year
      dateStrParts.push(this.$filter('numberSuffixFilter')(blockout.yearlyDay));
      dateStrParts.push(monthFmt.format(moment().month(Number(blockout.yearlyMonth)).toDate()));
    }

    return dateStrParts.join(' ');
  }

  public formatDates(blockout: IBlockout): string {
    const dateStrParts = [];
    const locale = LocaleService.getLocale();
    const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: 'long' });
    const monthFmt = new Intl.DateTimeFormat(locale, { month: 'long' });

    if (blockout.repeatType === BlockoutRepeatTypes.Daily) {
      dateStrParts.push(this.$filter('capitalizeFilter')(blockout.dailyRepeat));
    } else if (blockout.repeatType === BlockoutRepeatTypes.Weekly) {
      const days = [];
      for (const dayNum in blockout.weeklyDay) {
        const dayAllowed = blockout.weeklyDay[dayNum];
        if (dayAllowed) {
          days.push(weekdayFmt.format(moment().day(Number(dayNum)).toDate()) + 's');
        }
      }
      dateStrParts.push(days.join(', '));
    } else if (blockout.repeatType === BlockoutRepeatTypes.Monthly) {
      if (blockout.monthlyType === 'day') {
        dateStrParts.push(this.$filter('numberSuffixFilter')(blockout.monthlyOccurrence));
        dateStrParts.push(weekdayFmt.format(moment().day(Number(blockout.monthlyDay)).toDate()));
        dateStrParts.push('of the month,');
      } else {
        dateStrParts.push(this.$filter('numberSuffixFilter')(blockout.monthlyDate));
        dateStrParts.push('of the month,');
      }
    } else if (blockout.repeatType === BlockoutRepeatTypes.Yearly) {
      dateStrParts.push(this.$filter('numberSuffixFilter')(blockout.yearlyDay));
      dateStrParts.push(monthFmt.format(moment().month(Number(blockout.yearlyMonth)).toDate()));
    }
    if (!blockout.repeatType || blockout.repeatType === BlockoutRepeatTypes.NoRepeat) {
      // Combined datetime — keep time via Angular date filter, localise date portion
      dateStrParts.push(this.$filter('date')(blockout.startDateTime, 'h:mm a') + ' ' + LocaleService.formatDate(blockout.startDateTime, 'medium'));
      dateStrParts.push(' - ');
      dateStrParts.push(this.$filter('date')(blockout.endDateTime, 'h:mm a') + ' ' + LocaleService.formatDate(blockout.endDateTime, 'medium'));
    } else {
      dateStrParts.push(this.$filter('date')(blockout.startTime, 'h:mm a'));
      dateStrParts.push(' - ');
      dateStrParts.push(this.$filter('date')(blockout.endTime, 'h:mm a'));
      dateStrParts.push('from the ' + LocaleService.formatDate(blockout.startDate, 'medium'));
      if (blockout.endDate) {
        dateStrParts.push('until ' + LocaleService.formatDate(blockout.endDate, 'medium'));
      }
    }

    return dateStrParts.join(' ');
  }

  public getBlockoutsOnDate(date: Date): Promise<IBlockout[]> {
    return this.getAll().then((blockouts: IBlockout[]) => {
      const blockoutsOnDate: IBlockout[] = [];
      blockouts.forEach((blockout: IBlockout) => {
        if (
          blockout.repeatType === BlockoutRepeatTypes.NoRepeat ||
          blockout.repeatType === BlockoutRepeatTypes.Close
        ) {
          const startRef =
            blockout.repeatType === BlockoutRepeatTypes.NoRepeat
              ? blockout.startDateTime
              : blockout.startDate;
          const endRef =
            blockout.repeatType === BlockoutRepeatTypes.NoRepeat
              ? blockout.endDateTime
              : blockout.endDate;

          if (
            DateUtilsService.isOnSameDate(date, startRef as Date) ||
            DateUtilsService.isOnSameDate(date, endRef as Date) ||
            DateUtilsService.isInBetweenDates(date, startRef as Date, endRef as Date)
          ) {
            // the blackout occuers on this date
            blockoutsOnDate.push(blockout);
          }
        } else {
          // firstly make sure the date range is correct
          let allowedDate = false;
          if (!blockout.endDate && date >= blockout.startDate) {
            allowedDate = true;
          } else if (
            DateUtilsService.isOnOrInBetweenDates(
              date,
              blockout.startDate as Date,
              blockout.endDate as Date
            )
          ) {
            allowedDate = true;
          }
          // dont allow blockouts on exculded dates
          const datesStrArr: string[] = blockout.excludedDates.map(
            (excludedDate: string | Date) => {
              // return this.$filter('date')(excludedDate, 'yyyy-MM-dd');
              return DateUtilsService.dateToTimeStr(new Date(excludedDate), 'YYYY-MM-DD');
            }
          );

          // const dateStr = this.$filter('date')(date, 'yyyy-MM-dd');
          const dateStr = DateUtilsService.dateToTimeStr(new Date(date), 'YYYY-MM-DD');

          if (datesStrArr.indexOf(dateStr) !== -1) {
            allowedDate = false;
          }

          if (allowedDate) {
            let valid = false;
            const dayNum = date.getDay();
            if (blockout.repeatType === BlockoutRepeatTypes.Daily) {
              // check that the day of the week applies
              if (blockout.dailyRepeat === 'everyday') {
                valid = true;
              }
              if (blockout.dailyRepeat === 'weekdays' && dayNum !== 0 && dayNum !== 6) {
                valid = true;
              }
              if (blockout.dailyRepeat === 'weekends' && (dayNum === 0 || dayNum === 6)) {
                valid = true;
              }
            }
            if (blockout.repeatType === BlockoutRepeatTypes.Weekly) {
              if (blockout.weeklyDay[dayNum] === true) {
                valid = true;
              }
            }
            if (blockout.repeatType === BlockoutRepeatTypes.Monthly) {
              if (blockout.monthlyType === 'date' && date.getDate() === blockout.monthlyDate) {
                valid = true;
              }
              if (blockout.monthlyType === 'day') {
                // find the date to match
                const firstDayOfMonth = new Date(date.getTime());
                firstDayOfMonth.setDate(1);
                const monthlyDay = Number(blockout.monthlyDay);
                const occurrence = Number(blockout.monthlyOccurrence);
                const monthStartsOn = firstDayOfMonth.getDay();
                let dayOfMonth = (occurrence - 1) * 7;
                if (monthStartsOn === monthlyDay) {
                  dayOfMonth += 1;
                } else {
                  if (monthStartsOn >= monthlyDay) {
                    dayOfMonth += 7 - monthStartsOn + (monthlyDay + 1);
                  }
                  if (monthStartsOn <= monthlyDay) {
                    dayOfMonth -= monthStartsOn;
                    dayOfMonth += monthlyDay + 1;
                  }
                }

                const dateToMatch = new Date(firstDayOfMonth.getTime());
                dateToMatch.setDate(dayOfMonth);

                if (
                  dateToMatch.getDate() === date.getDate() &&
                  dateToMatch.getMonth() === date.getMonth()
                ) {
                  valid = true;
                }
              }
            }
            if (blockout.repeatType === BlockoutRepeatTypes.Yearly) {
              if (
                date.getMonth() === Number(blockout.yearlyMonth) &&
                date.getDate() === blockout.yearlyDay
              ) {
                valid = true;
              }
            }
            if (valid) {
              // format the blockout for this day
              const startDateTime = new Date(date.getTime());
              const temp1 = new Date(blockout.startTime);
              startDateTime.setHours(temp1.getHours());
              startDateTime.setMinutes(temp1.getMinutes());
              blockout.startDateTime = startDateTime;
              const endDateTime = new Date(date.getTime());
              const temp2 = new Date(blockout.endTime);
              endDateTime.setHours(temp2.getHours());
              endDateTime.setMinutes(temp2.getMinutes());
              if (endDateTime < startDateTime) {
                endDateTime.setDate(endDateTime.getDate() + 1);
              }
              blockout.endDateTime = endDateTime;
              blockoutsOnDate.push(blockout);
            }
          }
        }
      });
      return blockoutsOnDate;
    });
  }
}

const instance = new BlockOutsService();
export default instance as BlockOutsService;
